import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { WbmMainHeaderApi } from "@wbmason/main-header-api";
import {
  MiniCartIcon,
  WBMCartSlideInSelection,
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
} from "wbm-cart-slide-in-selection";
import { productUrlService } from "../../utilities/productUrlService";
import ConfigProps from "../../constants/configProps";
import NavigationTokens from "../../constants/navigationTokens";
import { LoginIntent } from "../../enums/LoginIntent";
import useLoginUrl from "../../hooks/useLoginUrl";
import { IAddressBudget } from "../../interfaces/IAddressBudget";
import { IAvailabilityInfo } from "../../interfaces/IAvailabilityInfo";
import { ICartItem } from "../../interfaces/ICartItem";
import { ICartTotals } from "../../interfaces/ICartTotals";
import { IDisplayState } from "../../interfaces/IDisplayState";
import { IItemIndicator } from "../../interfaces/IItemIndicator";
import { IShoppingLocation } from "../../interfaces/IShoppingLocation";
import { IUserBudget } from "../../interfaces/IUserBudget";
import { IWorkflow } from "../../interfaces/IWorkflow";
import ConfigPropService from "../../utilities/configPropService";
import DigitalDataService from "../../utilities/digitalDataService";
import NavigationTokensService from "../../utilities/navigationTokensService";
import OrderApprovalService from "../../utilities/orderApprovalService";
import ScrollBarService from "../../utilities/scrollBarService";
import Icon from "../icon/icon";
import "./cart.scss";
import { WBMUrlService } from "@wbmason/url-service";
import { BaseHostUrlService } from "../../utilities/baseHostUrlService";
import EnvironmentService from "../../utilities/environmentService";
import AddressSelectWrapper from "../addressSelect/indexWrapper";

export interface ICartProps {
  cartTotals: ICartTotals;
  hasItemAgreements: boolean;
  checkoutAppUri: string;
  isLoggedIn: boolean;
  isCookied: boolean;
  shoppingLocation?: IShoppingLocation;
  setShoppingLocation: (shoppingLocation: IShoppingLocation) => void;
  workflow: IWorkflow;
  budgetCallback: (addressBudget: IAddressBudget) => void;
  displayState: IDisplayState;
  addressBudget?: IAddressBudget;
  userBudget?: IUserBudget;
  cartId: string;
  hasMarketingDiv: boolean;
  safeZoneHeight: number;
  itemIndicators: IItemIndicator[];
  availabilityInfo: IAvailabilityInfo[];
  removeItemCallback: (cartItem: ICartItem) => void;
  hasRemoveCartError?: boolean;
}

function Cart(props: ICartProps) {
  const [navTokens, setNavTokens] = useState<Record<string, string>>();
  const [configProps, setConfigProps] = useState<Record<string, string>>();
  const [workflowSaveDisabled, setWorkflowSaveDisabled] = useState(false);
  const [checkoutUrl, setCheckoutUrl] = useState("");
  const loginUrl = useLoginUrl(LoginIntent.Checkout);
  const [forceOpen, setForceOpen] = useState(false);
  const [shoppingCartUrl, setShoppingCartUrl] = useState<string>("");
  const [ecomUrl, setEcomUrl] = useState<string>("");
  const navTokensResolved = useRef<boolean>(false);

  const cartItems =
    props.cartTotals && props.cartTotals.items ? props.cartTotals.items : [];

  const orderTotals = props.cartTotals
    ? props.cartTotals
    : {
        subTotal: 0,
        totalAmount: 0,
      };

  useMemo(() => {
    if (!navTokens || !configProps) {
      return;
    }

    let checkoutUrl;

    const usePunchout = ConfigPropService.isYesOrTrue(
      configProps[ConfigProps.Use_Punchout]
    );
    const enableSinglePageCheckout = ConfigPropService.isYesOrTrue(
      configProps[ConfigProps.EnableSinglePageCheckout]
    );
    const useExternalIdentityProvider = ConfigPropService.isYesOrTrue(
      configProps[ConfigProps.UseExternalIdentityProvider]
    );

    if (props.hasItemAgreements) {
      checkoutUrl = shoppingCartUrl; // user must agree to terms
    } else if (usePunchout) {
      checkoutUrl = navTokens[NavigationTokens.Punchout]; // redirect to punchout page
    } else if (props.checkoutAppUri && enableSinglePageCheckout) {
      if (props.isLoggedIn) {
        checkoutUrl = props.checkoutAppUri; // checkout app uri should go direct to checkout
      } else if (useExternalIdentityProvider) {
        checkoutUrl = props.checkoutAppUri; // checkout app uri should be IDP link that redirects to checkout
      } else {
        checkoutUrl = loginUrl; // require user to login
      }
    } else {
      checkoutUrl = navTokens[NavigationTokens.ShippingAddress]; // go to old checkout
    }

    setCheckoutUrl(checkoutUrl || "");
  }, [
    navTokens,
    configProps,
    props.checkoutAppUri,
    props.hasItemAgreements,
    props.isLoggedIn,
    loginUrl,
    shoppingCartUrl,
  ]);

  useEffect(() => {
    const navTokenSub = NavigationTokensService.get([
      NavigationTokens.ShoppingCart,
      NavigationTokens.SinglePageCheckout,
      NavigationTokens.ShippingAddress,
      NavigationTokens.home,
      NavigationTokens.Punchout,
      NavigationTokens.OrderAwaitingApprovalDetails,
    ]).subscribe((navTokens) => {
      setNavTokens(navTokens);
    });

    const configPropSub = ConfigPropService.get([
      ConfigProps.EnableSinglePageCheckout,
      ConfigProps.DisplayBudgetDetailsWhileShopping,
      ConfigProps.NoImageAvailableImage,
      ConfigProps.Use_Punchout,
      ConfigProps.UseExternalIdentityProvider,
      ConfigProps.EnableResponsiveSearchResults,
      ConfigProps.EnableResponsiveShoppingCart,
    ]).subscribe((configProps) => {
      setConfigProps(configProps);
    });

    const ecomUrl = WBMUrlService.getEcomUrl(
      BaseHostUrlService.get(),
      EnvironmentService.get().ecom
    );

    setEcomUrl(ecomUrl);

    return () => {
      navTokenSub.unsubscribe();
      configPropSub.unsubscribe();
    };
  }, []);

  const closeCallback = useCallback(() => {
    setForceOpen(false);
  }, [setForceOpen]);

  useEffect(() => {
    const openCartSlideout = WbmMainHeaderApi.getCartSlideout().subscribe(
      (open) => {
        setForceOpen(open);
      }
    );

    return () => {
      openCartSlideout.unsubscribe();
    };
  });

  useEffect(() => {
    if (cartItems && cartItems.length > 0 && configProps) {
      const enableResponsiveSearchResults = ConfigPropService.isYesOrTrue(
        configProps[ConfigProps.EnableResponsiveSearchResults]
      );

      cartItems.forEach((cartItem) => {
        cartItem.productDetailsUrl = productUrlService.getItemHref(
          cartItem.itemIdentifier,
          enableResponsiveSearchResults
        );
      });
    }
  }, [cartItems, configProps]);

  useEffect(() => {
    let shoppingCartUrl: string;

    if (navTokens && navTokens[NavigationTokens.ShoppingCart] && configProps) {
      if (
        ConfigPropService.isYesOrTrue(
          configProps[ConfigProps.EnableResponsiveShoppingCart]
        )
      ) {
        shoppingCartUrl = WBMUrlService.getShoppingCartUrl(
          BaseHostUrlService.get(),
          EnvironmentService.get().ecomShoppingCart
        );
      } else {
        shoppingCartUrl = navTokens[NavigationTokens.ShoppingCart];
      }

      setShoppingCartUrl(shoppingCartUrl);
      navTokensResolved.current = true;
    }
  }, [navTokens, configProps]);

  const applySplitLine = (
    cartLineRecId: unknown,
    existingQuantity: unknown,
    quantity: unknown,
    note: unknown
  ) => {
    let returnString = "CartLineRecId= " + cartLineRecId;
    returnString += " | existingQuantity= " + existingQuantity;
    returnString += " | quantity= " + quantity;
    returnString += " | note= " + note;
    alert(returnString);
  };

  const onApplyAllocatedItems = (lineItemsList: unknown) => {
    alert(lineItemsList);
  };

  const saveSelectedAddress = (/* id: number | string */) => {
    // todo setSelectedAddressId(id.toString());
  };
  const renderHeader = () => {
    return (
      <AddressSelectWrapper
        isSecondary={true}
        isLoggedIn={props.isLoggedIn}
        isCookied={props.isCookied}
        shoppingLocation={props.shoppingLocation}
        setShoppingLocation={props.setShoppingLocation}
        workflow={props.workflow}
        budgetCallback={props.budgetCallback}
        appendRemoveContainerWidth={(isOpen: boolean) => {
          ScrollBarService.applyModalOpen(isOpen, "address select", true);
        }}
        displayState={props.displayState}
        isSecondarySlideIn={true}
        renderTrigger={(currrentFormattedAddress) => (
          <div className="cart-change-address">
            <Icon className="align-middle">location_on</Icon>
            <span>
              {currrentFormattedAddress ? (
                <b>{currrentFormattedAddress}</b>
              ) : (
                <b>Select An Address</b>
              )}
            </span>
          </div>
        )}
      />
    );
  };

  let noImageUrl = "";
  if (configProps) {
    if (configProps[ConfigProps.NoImageAvailableImage].length > 0) {
      noImageUrl = configProps[ConfigProps.NoImageAvailableImage];
    }
  }

  const resolveBudget = () => {
    // prefer address budget over user budget
    if (props.addressBudget) {
      return {
        availableBudget: props.addressBudget.availableBudget,
        remainingBudget: props.addressBudget.remainingBudget,
        displayBudgetDetailsWhileShopping: configProps
          ? ConfigPropService.isYesOrTrue(
              configProps[ConfigProps.DisplayBudgetDetailsWhileShopping]
            )
          : false,
        forceOverBudget:
          props.cartTotals.subTotal > props.addressBudget.remainingBudget,
      };
    }

    if (props.cartTotals && props.userBudget) {
      return {
        availableBudget: props.userBudget.availableBudget,
        remainingBudget: props.userBudget.remainingBudget,
        displayBudgetDetailsWhileShopping: configProps
          ? ConfigPropService.isYesOrTrue(
              configProps[ConfigProps.DisplayBudgetDetailsWhileShopping]
            )
          : false,
      };
    }

    return null;
  };

  const getWorkflow = () => {
    let workflow = props.workflow;

    if (workflow) {
      workflow = Object.assign(workflow, {
        saveButtonProps: {
          onClick: () => {
            setWorkflowSaveDisabled(true);
            if (!props.isCookied) {
              OrderApprovalService.saveWorkflow().subscribe(function () {
                if (navTokens) {
                  window.location.href =
                    navTokens[NavigationTokens.OrderAwaitingApprovalDetails] +
                    "?SID=" +
                    props.workflow.orderNumber +
                    "&CartID=" +
                    props.cartId;
                }
              });
            }
          },
          disabled: workflowSaveDisabled,
        },
        workFlowRedirect: props.isCookied && navTokens ? loginUrl : undefined,
      });
    }

    return workflow;
  };

  let cartItemCount = 0;
  if (cartItems) {
    for (let i = 0; i < cartItems.length; i++) {
      if (cartItems[i].lotQty > 0) {
        cartItemCount += 1;
      } else {
        cartItemCount += cartItems[i].qty;
      }
    }
  }

  return (
    <div className={`no-print ${navTokensResolved.current ? "" : "inactive"}`}>
      <WBMCartSlideInSelection
        forceOpen={forceOpen}
        closeCallback={closeCallback}
        cartItems={cartItems ? cartItems : []}
        adobeEventsConfig={{ proceedToCheckout: true, cartViewed: false }}
        ecomHomePageURL={navTokens && navTokens[NavigationTokens.home]}
        cartTrigger={() => (
          <div
            className={props.hasMarketingDiv ? "marketing-offset" : ""}
            onClick={() => {
              // this function only has side effects within ecom
              DigitalDataService.setEvent({
                event: "cartViewed",
              });

              // this function only has side effects outside of ecom
              DigitalDataService.viewCart(props.cartTotals);
            }}
          >
            <div style={{ height: props.safeZoneHeight }}></div>
            <MiniCartIcon
              cartItemCount={cartItemCount}
              onSelectedAddressChanged={saveSelectedAddress}
              cartIconOverride={
                <Icon
                  className={
                    "suspense-cart" +
                    (props.workflow && props.workflow.isWorkflow
                      ? " workflow-cart"
                      : "")
                  }
                  tabIndex={0}
                  renderAnchorTag
                >
                  shopping_cart
                </Icon>
              }
            />
          </div>
        )}
        cartTotals={orderTotals}
        budget={resolveBudget()}
        header={renderHeader()}
        orderTotals={orderTotals}
        shoppingCartUrl={shoppingCartUrl}
        editShoppingCartUrl={shoppingCartUrl}
        removeItemCallback={props.removeItemCallback}
        checkoutUrl={checkoutUrl}
        //lineLevelAllocation
        //lineLevelSelection
        onApplySplitLine={applySplitLine}
        totalCartItems={cartItems.length}
        onApplyAllocatedItems={onApplyAllocatedItems}
        itemIndicators={props.itemIndicators}
        //allocations={allocatedItems}
        workflow={getWorkflow()}
        containerId={"body"}
        noImageUrl={noImageUrl}
        availabilityInfo={props.availabilityInfo}
        checkoutButtonText={props.hasItemAgreements ? "Continue to cart" : null}
        appendRemoveContainerWidth={(isOpen: boolean) => {
          ScrollBarService.applyModalOpen(isOpen, "cart slide out", true);
        }}
        isCookied={props.isCookied}
        showNavInkLotQtyMessage
        ecomUrl={ecomUrl}
        hasRemoveCartError={props.hasRemoveCartError}
      />
    </div>
  );
}

export default Cart;
